// Frontend/src/component/demodashboard/flowdesigners/FlowDesigners.jsx
import { useState } from "react";
import FlowCanvas from "./FlowCanvas";
import NodeConfigPanel from "./panels/NodeConfigPanel";
import { useEmailFlowLogic } from "./useEmailFlowLogic";
import styles from "./flowdesigners.module.css";

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

export default function FlowDesigners() {
  const logic = useEmailFlowLogic(); // This should return: { toEmail, subject, emailBody, setEmailBody, handleGenerate, handleSendEmail, ... }

  const [activeNodeId, setActiveNodeId] = useState(null); // Start with nothing selected
  const [activeEdgeId, setActiveEdgeId] = useState(null);
  const [completedEdges, setCompletedEdges] = useState([]);

  const [nodeStatus, setNodeStatus] = useState({
    trigger: "idle",
    generate: "idle",
    gemini: "idle",
    review: "idle",
    send: "idle",
  });

  // Start the entire flow
  const startFlow = async () => {
    setNodeStatus((s) => ({ ...s, trigger: "running" }));
    setActiveEdgeId("e-trigger-generate");
    setActiveNodeId("generate"); // Auto-select generate node

    await sleep(400);
    setNodeStatus((s) => ({ ...s, trigger: "done" }));
    setCompletedEdges(["e-trigger-generate"]);
  };

  // Generate email using Gemini
  const startGenerate = async () => {
    setNodeStatus((s) => ({ ...s, generate: "running", gemini: "running" }));
    setActiveEdgeId("e-generate-gemini");

    try {
      await logic.handleGenerate(); // This should update logic.toEmail, subject, emailBody
    } catch (err) {
      console.error("Generate failed:", err);
      alert("Failed to generate email. Please try again.");
      setNodeStatus((s) => ({ ...s, generate: "idle", gemini: "idle" }));
      return;
    }

    setNodeStatus((s) => ({
      ...s,
      generate: "done",
      gemini: "done",
    }));

    // Move forward to Review
    setActiveEdgeId("e-generate-review");
    setCompletedEdges((prev) => [...prev, "e-generate-gemini", "e-generate-review"]);
    setActiveNodeId("review");
  };

  // Send final email
  const sendEmail = async () => {
    if (!logic.toEmail || !logic.emailBody?.trim()) {
      alert("Please fill in recipient and email body.");
      return;
    }

    setNodeStatus((s) => ({ ...s, send: "running" }));
    setActiveEdgeId("e-review-send");

    try {
      await logic.handleSendEmail();
      setNodeStatus((s) => ({ ...s, send: "done" }));
      setCompletedEdges((prev) => [...prev, "e-review-send"]);
      setActiveNodeId("send");
    } catch (err) {
      console.error("Send failed:", err);
      alert("Failed to send email.");
      setNodeStatus((s) => ({ ...s, send: "idle" }));
    }
  };

  return (
    <div className={styles.flowDesignerWrapper}>
      {/* LEFT: FLOW CANVAS */}
      <div className={styles.canvasArea}>
        <FlowCanvas
          activeNodeId={activeNodeId}
          nodeStatus={nodeStatus}
          activeEdgeId={activeEdgeId}
          completedEdges={completedEdges}
          onSelectNode={setActiveNodeId}
          onTrigger={startFlow}
          onGenerate={startGenerate}
          onSend={sendEmail}
          // No need to pass logic here unless a node uses it directly
        />
      </div>

      {/* RIGHT: NODE CONFIG PANEL */}
      <div className={styles.configArea}>
        <NodeConfigPanel
          activeNodeId={activeNodeId}
          logic={logic}
          onContinueToReview={() => setActiveNodeId("review")} // fallback if needed
          onSend={sendEmail}
        />
      </div>
    </div>
  );
}